import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, BirthdayFunFact } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

const DEFAULT_FACTS: BirthdayFunFact[] = [
  { label: 'Favorite Food', value: '🍕 Pizza' },
  { label: 'Favorite Cartoon', value: '🦸 Superheroes' },
  { label: 'Favorite Color', value: '💙 Blue' },
  { label: 'Favorite Toy', value: '🚗 Cars' },
];

function FactCard({ fact, index }: { fact: BirthdayFunFact; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: BDAY_EASE }}
      whileHover={{ y: -6, scale: 1.03 }}
      className="w-44 sm:w-52 bg-white/5 backdrop-blur-sm border border-blue-400/20 rounded-2xl p-5 flex flex-col gap-2 shadow-md group"
    >
      <p className="text-blue-400 text-xs font-bold uppercase tracking-widest">{fact.label}</p>
      <p className="text-white text-lg font-semibold group-hover:text-blue-100 transition-colors">{fact.value}</p>
    </motion.div>
  );
}

export function FunFactsSection({ content }: { content: BirthdayContent }) {
  const facts = content.funFacts.length ? content.funFacts : DEFAULT_FACTS;

  const bg = content.sectionBackgrounds.funFacts;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-24 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%)' }}
    >
      <SectionBgImage bg={bg} />
      <div className="max-w-3xl mx-auto">
        {/* heading */}
        <div className="text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: BDAY_EASE }}
            className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
          >
            🎉 All About Them
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
            className="text-3xl sm:text-4xl font-bold text-white"
          >
            Fun Facts
          </motion.h2>
        </div>

        {/* grid */}
        <div className="flex flex-wrap justify-center gap-4">
          {facts.map((fact, i) => (
            <FactCard key={i} fact={fact} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
